import { useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Card, Input } from '@bancobarigui/barihaus';
import { Question, QuestionType, Group, DisplayRule } from '../types';
import { QuestionHeader } from './QuestionHeader';
import { QuestionFooter } from './QuestionFooter';
import { DisplayRuleModal, DisplayRuleDescription } from './components';
import { useQuestionToggles } from './hooks';
import {
  TextQuestionConfig,
  DecimalQuestionConfig,
  IntegerQuestionConfig,
  CurrencyQuestionConfig,
  PhotoQuestionConfig,
  FileQuestionConfig,
  TableQuestionConfig,
  OptionsBasedQuestionConfig,
  YesNoQuestionConfig,
  DateQuestionConfig,
} from './config-sections';

interface QuestionItemProps {
  question: Question;
  index: number;
  onEdit: (questionId: string, updates: Partial<Question>) => void;
  onRemove: (questionId: string) => void;
  onDuplicate: (questionId: string) => void;
  allGroups?: Group[];
}

export function QuestionItem({
  question,
  index,
  onEdit,
  onRemove,
  onDuplicate,
}: QuestionItemProps) {
  const [isDisplayRuleModalOpen, setIsDisplayRuleModalOpen] = useState(false);

  const { toggleRequired, toggleShowInReport, toggleHint } = useQuestionToggles({
    question,
    onEdit,
  });

  const handleOpenDisplayRuleModal = () => {
    setIsDisplayRuleModalOpen(true);
  };

  const handleCloseDisplayRuleModal = () => {
    setIsDisplayRuleModalOpen(false);
  };

  const handleSaveDisplayRule = (rule: DisplayRule) => {
    onEdit(question.id, {
      hasDisplayRule: true,
      displayRule: rule,
    });
  };

  const handleRemoveDisplayRule = () => {
    if (!question.hasDisplayRule) {
      handleOpenDisplayRuleModal();
    } else {
      const updates: Partial<Question> = {
        hasDisplayRule: false,
      };
      delete (updates as any).displayRule;
      onEdit(question.id, updates);
    }
  };

  const renderQuestionConfig = () => {
    const { answerType } = question;

    if (answerType === QuestionType.SHORT_TEXT || answerType === QuestionType.LONG_TEXT) {
      return <TextQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.RADIO || answerType === QuestionType.CHECKBOX) {
      return <OptionsBasedQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.YES_NO) {
      return <YesNoQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.DATE) {
      return <DateQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.INTEGER) {
      return <IntegerQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.DECIMAL) {
      return <DecimalQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.CURRENCY) {
      return <CurrencyQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.PHOTO) {
      return <PhotoQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.FILE) {
      return <FileQuestionConfig question={question} onEdit={onEdit} />;
    }

    if (answerType === QuestionType.TABLE) {
      return <TableQuestionConfig question={question} onEdit={onEdit} />;
    }

    return null;
  };

  return (
    <Draggable draggableId={`question-${question.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-3 ${snapshot.isDragging ? 'opacity-50' : ''}`}
        >
          <Card
            className="border border-gray-200 bg-white"
            bodyStyle={{ padding: '16px' }}
          >
            <QuestionHeader
              question={question}
              onEdit={onEdit}
              dragHandleProps={provided.dragHandleProps}
            />

            <div className="space-y-3">
              {renderQuestionConfig()}

            </div>

            <div className='flex flex-col gap-4'>
              {question.hasHint && (
                <div className="mt-3">
                  {' '}
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Dica
                  </label>
                  <Input
                    placeholder="Digite a dica para auxiliar na resposta..."
                    value={question.hintText || ''}
                    onChange={(e) => onEdit(question.id, { hintText: e.target.value })}
                    className="text-sm"
                  />
                </div>
              )}
              {question.hasDisplayRule && question.displayRule && (
                <DisplayRuleDescription
                  rule={question.displayRule}
                  onClick={handleOpenDisplayRuleModal}
                />
              )}
            </div>
            <QuestionFooter
              question={question}
              onRemove={() => onRemove(question.id)}
              onDuplicate={() => onDuplicate(question.id)}
              toggleRequired={toggleRequired}
              toggleShowInReport={toggleShowInReport}
              toggleHint={toggleHint}
              toggleDisplayRule={handleRemoveDisplayRule}
            />
          </Card>

          <DisplayRuleModal
            visible={isDisplayRuleModalOpen}
            onClose={handleCloseDisplayRuleModal}
            onSave={handleSaveDisplayRule}
            currentRule={question.displayRule}
          />
        </div>
      )}
    </Draggable>
  );
}
